import React from 'react';

const PreviewRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="mb-1">
      <span className="preview-label">{label}: </span>
      <span className="preview-value">{value}</span>
    </div>
  );
};

const SoaPreview = ({ formData, selectedPortfolio }) => {
  const { clientDetails, scope, currentSituation, objectives, riskProfile, strategy, fees, warnings } = formData;

  const totalAssets = (currentSituation.assets || []).reduce((s, a) => s + (parseFloat(a.value) || 0), 0);
  const totalLiabilities = (currentSituation.liabilities || []).reduce((s, l) => s + (parseFloat(l.value) || 0), 0);

  const adviserFee = parseFloat(fees.adviserFee) || 0;
  const platformFee = parseFloat(fees.platformFee) || 0;
  const portfolioIcr = parseFloat(fees.portfolioIcr) || 0;
  const totalOngoing = adviserFee + platformFee + portfolioIcr;

  return (
    <div className="soa-preview">
      <h5 style={{ color: 'var(--primary-color)', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
        <i className="bi bi-eye me-1"></i>Document Preview
      </h5>

      {/* Client */}
      <h6>Client Details</h6>
      <PreviewRow label="Name" value={clientDetails.fullName} />
      <PreviewRow label="DOB" value={clientDetails.dateOfBirth} />
      <PreviewRow label="Employment" value={clientDetails.employment} />
      {clientDetails.isCouple && <PreviewRow label="Partner" value={clientDetails.partnerName} />}
      {clientDetails.dependants && <PreviewRow label="Dependants" value={clientDetails.dependants} />}

      {/* Scope */}
      {scope.adviceCovered && (
        <>
          <h6>Scope of Advice</h6>
          <p style={{ fontSize: '0.78rem', color: '#555' }}>{scope.adviceCovered.substring(0, 150)}{scope.adviceCovered.length > 150 ? '...' : ''}</p>
        </>
      )}

      {/* Situation */}
      {(totalAssets > 0 || totalLiabilities > 0) && (
        <>
          <h6>Financial Position</h6>
          {totalAssets > 0 && <PreviewRow label="Total Assets" value={`$${totalAssets.toLocaleString('en-AU')}`} />}
          {totalLiabilities > 0 && <PreviewRow label="Total Liabilities" value={`$${totalLiabilities.toLocaleString('en-AU')}`} />}
          <PreviewRow label="Net Position" value={`$${(totalAssets - totalLiabilities).toLocaleString('en-AU')}`} />
        </>
      )}

      {/* Objectives */}
      {(objectives.retirementAge || objectives.incomeNeeds) && (
        <>
          <h6>Objectives</h6>
          <PreviewRow label="Retirement Age" value={objectives.retirementAge} />
          {objectives.incomeNeeds && <PreviewRow label="Income Need" value={`$${parseFloat(objectives.incomeNeeds).toLocaleString('en-AU')} p.a.`} />}
          <PreviewRow label="Time Horizon" value={objectives.timeHorizon ? `${objectives.timeHorizon} years` : ''} />
        </>
      )}

      {/* Risk Profile */}
      {riskProfile.selectedProfile && (
        <>
          <h6>Risk Profile</h6>
          <span
            className="badge"
            style={{
              backgroundColor: riskProfile.selectedProfile === 'Conservative' ? 'var(--info-color)' :
                riskProfile.selectedProfile === 'Moderate' ? 'var(--success-color)' :
                riskProfile.selectedProfile === 'Balanced' ? 'var(--secondary-color)' :
                riskProfile.selectedProfile === 'Growth' ? '#e67e22' : '#dc3545',
              color: '#fff',
              fontSize: '0.75rem',
            }}
          >
            {riskProfile.selectedProfile}
          </span>
        </>
      )}

      {/* Strategy */}
      {selectedPortfolio && (
        <>
          <h6>Recommendation</h6>
          <PreviewRow label="Portfolio" value={selectedPortfolio.display_name} />
          {selectedPortfolio.growth_exposure !== null && (
            <>
              <div style={{ display: 'flex', height: '14px', borderRadius: '3px', overflow: 'hidden', margin: '4px 0' }}>
                <div style={{ width: `${Math.round(selectedPortfolio.growth_exposure * 100)}%`, backgroundColor: 'var(--primary-color)' }}></div>
                <div style={{ width: `${100 - Math.round(selectedPortfolio.growth_exposure * 100)}%`, backgroundColor: 'var(--info-color)' }}></div>
              </div>
              <div className="d-flex justify-content-between" style={{ fontSize: '0.7rem', color: '#888' }}>
                <span>Growth {Math.round(selectedPortfolio.growth_exposure * 100)}%</span>
                <span>Defence {100 - Math.round(selectedPortfolio.growth_exposure * 100)}%</span>
              </div>
            </>
          )}
        </>
      )}

      {/* Fees */}
      {totalOngoing > 0 && (
        <>
          <h6>Fees</h6>
          <PreviewRow label="Adviser" value={`${adviserFee.toFixed(2)}%`} />
          <PreviewRow label="Platform" value={`${platformFee.toFixed(2)}%`} />
          <PreviewRow label="ICR" value={`${portfolioIcr.toFixed(2)}%`} />
          <div style={{ backgroundColor: 'var(--primary-color)', color: '#fff', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, marginTop: '4px' }}>
            Total: {totalOngoing.toFixed(2)}% p.a.
          </div>
        </>
      )}

      {/* Adviser */}
      {warnings.adviserName && (
        <>
          <h6>Adviser</h6>
          <PreviewRow label="Name" value={warnings.adviserName} />
          <PreviewRow label="AFSL" value={warnings.afslNumber} />
        </>
      )}
    </div>
  );
};

export default SoaPreview;
